/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import Link from 'next/link';
import Image from 'next/image';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import ThemedButton from '../common/buttons/Button';
import ServicesMenu from './ServicesMenu';
import UserMenu from './UserMenu';
import MobileMenu from './header/MobileMenu';
import AuthSkeleton from './header/AuthSkeleton';
import { markAuthReady, useAuthReady } from '@/utils/authReady';
import styles from './header.module.scss';
import { usePathname } from 'next/navigation';

import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { setTokenFromStorage, setUser } from '@/redux/auth/signin/slice';
import { stripBase, imageSrc } from '@/utils/runtimeOrg';
import MyAccountDropdown from './MyAccountDropdown';

export type HeaderLink = {
  title: string;
  url?: string;
  external?: boolean;
  kind?: 'services' | 'link' | string;
  children?: HeaderLink[];
};

type HeaderProps = {
  authHint?: boolean;
  cfg: {
    name: string;
    logo: string;
    header?: { links?: HeaderLink[] };
  };
};

const GUTTER = 16;
const CARET_PX = 12;
const UNDERLINE_GAP = 6;
const UNDERLINE_THICKNESS = 2;
const CARET_GAP = 2;
const HOVER_CLOSE_DELAY = 200;

const triggerBase = `
  relative flex items-center gap-1 cursor-pointer whitespace-nowrap px-2 pb-2 text-sm outline-none
  text-[#1b2d2a] hover:text-[var(--color-accent)] active:text-[var(--color-accent)]
  transition-colors duration-200
  after:content-[''] after:absolute after:bottom-[-12px] after:left-0 after:w-full after:h-[2px]
  after:bg-[var(--color-accent)] after:scale-x-0 hover:after:scale-x-100 active:after:scale-x-100
  after:transition-transform after:duration-300 after:origin-left
  focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]/40 focus-visible:rounded
`;

const linkBase = triggerBase;

function getHeaderHeight(): number {
  if (typeof window === 'undefined') return 64;
  const css = getComputedStyle(document.documentElement).getPropertyValue(
    '--header-height'
  );
  const parsed = parseInt(css || '0', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 64;
}

function isAbsoluteUrl(href?: string) {
  return !!href && /^https?:\/\//i.test(href);
}

function NavLink({ href, label }: { href: string; label: string }) {
  const pathname = usePathname();
  const current = stripBase(pathname);
  const to = href;
  const isActive =
    current === href || (href !== '/' && current.startsWith(href));
  return (
    <Link
      href={to}
      className={`${styles.navLink} ${isActive ? styles.active : ''}`}
    >
      {label}
    </Link>
  );
}

function HeaderDropdown({ item }: { item: HeaderLink }) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const caretRef = useRef<HTMLDivElement | null>(null);
  const hoverCloseTimer = useRef<number | null>(null);

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };
  const scheduleHoverClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = window.setTimeout(
      () => setIsOpen(false),
      HOVER_CLOSE_DELAY
    ) as unknown as number;
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!isOpen) return;
      const t = e.target as Node;
      if (!panelRef.current?.contains(t) && !triggerRef.current?.contains(t)) {
        setIsOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    positionPanel();
    const onResize = () => positionPanel();
    const onScroll = () => positionPanel();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });

    queueMicrotask(() => {
      const first =
        panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
      first?.focus();
    });

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isOpen, item]);

  useEffect(() => {
    return () => {
      if (hoverCloseTimer.current) {
        window.clearTimeout(hoverCloseTimer.current);
        hoverCloseTimer.current = null;
      }
    };
  }, []);

  function getHeaderContainerRect(): DOMRect {
    const el = document.querySelector(
      '[data-header-container]'
    ) as HTMLElement | null;
    if (el) return el.getBoundingClientRect();
    return new DOMRect(GUTTER, 0, window.innerWidth - GUTTER * 2, 0);
  }

  const positionPanel = () => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    const caret = caretRef.current;
    if (!trigger || !panel || !caret) return;

    const headerH = getHeaderHeight();
    const t = trigger.getBoundingClientRect();
    const desiredCenter = t.left + t.width / 2;

    const underlineBottom = t.bottom + UNDERLINE_GAP + UNDERLINE_THICKNESS;
    const caretTop = Math.max(headerH + 2, underlineBottom + CARET_GAP);
    const panelTop = caretTop + CARET_PX / 2;

    const containerRect = getHeaderContainerRect();
    const containerLeft = Math.max(GUTTER, containerRect.left);
    const containerWidth = Math.min(
      window.innerWidth - GUTTER * 2,
      containerRect.width || window.innerWidth - GUTTER * 2
    );

    const origVis = panel.style.visibility;
    const origDisp = panel.style.display;
    panel.style.visibility = 'hidden';
    panel.style.display = 'block';

    panel.style.left = `${containerLeft}px`;
    panel.style.top = `${panelTop}px`;
    panel.style.width = `${containerWidth}px`;

    panel.style.visibility = origVis || '';
    panel.style.display = origDisp || '';

    const caretLeft = Math.max(
      containerLeft + CARET_PX / 2,
      Math.min(containerLeft + containerWidth - CARET_PX / 2, desiredCenter)
    );

    caret.style.left = `${caretLeft - CARET_PX / 2}px`;
    caret.style.top = `${caretTop - CARET_PX / 2}px`;
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen((v) => !v);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []
    );
    if (!items.length) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  const panelId = `header-panel-${item.title
    .replace(/\s+/g, '-')
    .toLowerCase()}`;
  const children = item.children || [];

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => {
        cancelHoverClose();
        setIsOpen(true);
      }}
    >
      <button
        ref={triggerRef}
        id={`${panelId}-trigger`}
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        onMouseEnter={cancelHoverClose}
        onMouseLeave={scheduleHoverClose}
        className={linkBase}
      >
        <span>{item.title}</span>
        <FaChevronDown
          className={`text-xs transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      {isOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9997]"
            aria-hidden={false}
            onMouseEnter={cancelHoverClose}
            onMouseLeave={scheduleHoverClose}
          >
            <div
              ref={caretRef}
              className="fixed z-[9999] h-3 w-3 rotate-45 bg-white border border-gray-200 shadow-sm"
              style={{ left: 0, top: 0 }}
            />
            <div
              ref={panelRef}
              id={panelId}
              role="menu"
              aria-label={item.title}
              aria-labelledby={`${panelId}-trigger`}
              tabIndex={-1}
              onKeyDown={onPanelKeyDown}
              className="
                fixed z-[9998]
                rounded-2xl border border-gray-200 bg-white
                shadow-[0_12px_30px_-6px_rgba(0,0,0,0.15)]
                ring-1 ring-black/5
                px-4 sm:px-6 md:px-10 py-4
                text-[#1b2d2a]
                overflow-y-auto -mt-2
              "
              style={
                {
                  maxHeight: `calc(100vh - ${getHeaderHeight()}px - 24px)`,
                } as React.CSSProperties
              }
            >
              <div className="grid gap-x-6 gap-y-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                {children.map((child: HeaderLink, idx: number) => {
                  const href = child.url || '#';
                  const external = child.external || isAbsoluteUrl(href);
                  const cls =
                    'block rounded-md px-2 py-2 text-[13.5px] leading-6 text-[#4a4a4a] hover:bg-gray-50 hover:text-[var(--color-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]/30';
                  return external ? (
                    <a
                      key={idx}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      className={cls}
                    >
                      {child.title}
                    </a>
                  ) : (
                    <Link key={idx} href={href} role="menuitem" className={cls}>
                      {child.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

function ConfigNav({ links }: { links: HeaderLink[] }) {
  return (
    <ul className="flex flex-col lg:flex-row mb-2 lg:mb-0 space-y-2 lg:space-y-0 lg:space-x-4 xl:space-x-6">
      {links.map((item, i) => {
        if (item.kind === 'services') {
          return (
            <li key={i} className="relative inline-block">
              <ServicesMenu />
            </li>
          );
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          return (
            <li key={i} className="relative inline-block">
              <HeaderDropdown item={item} />
            </li>
          );
        }
        const href = item.url || '#';
        const external = item.external || isAbsoluteUrl(href);
        return (
          <li key={i}>
            {external ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={linkBase}
              >
                <span>{item.title}</span>
              </a>
            ) : (
              <Link href={href} className={linkBase}>
                <span>{item.title}</span>
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function Header({ cfg, authHint = false }: HeaderProps) {
  const dispatch = useAppDispatch();

  const headerLinks: HeaderLink[] | undefined = (cfg as any)?.header?.links;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const authChecked = useAuthReady();

  const { token, user } = useAppSelector((s: any) => s.auth?.signin ?? {});
  const isLoggedIn = Boolean(token?.access);
  const username = user?.first_name || user?.username || 'User';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!token?.access) {
      const access = localStorage.getItem('accessToken');
      const refresh = localStorage.getItem('refreshToken');
      if (access && refresh) dispatch(setTokenFromStorage({ access, refresh }));
    }

    if (!user) {
      const raw = localStorage.getItem('userProfile');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          dispatch(setUser(parsed));
        } catch {}
      }
    }

    markAuthReady();
  }, [dispatch, token?.access, user]);

  const toggleMobileMenu = () => setMobileMenuOpen((v) => !v);
  const toggleServices = () => setServicesOpen((v) => !v);

  return (
    <header className="bg-[#FFFCEE] text-black sticky top-0 z-50 shadow-sm">
      <div
        data-header-container
        className="container mx-auto flex h-(--header-height) items-center justify-between gap-4 px-4"
      >
        <Link href="/" aria-label="Home" className="flex shrink-0 items-center">
          {typeof cfg.logo === 'string' ? (
            <Image
              src={imageSrc(cfg.logo)}
              alt={cfg.name}
              width={148}
              height={60}
              className="h-10 w-auto sm:h-12"
              priority
            />
          ) : (
            <div className="flex items-center">{cfg.logo}</div>
          )}
        </Link>

        <button
          onClick={toggleMobileMenu}
          className="text-xl text-[#1b2d2a] lg:hidden"
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <nav
          className={`${styles.navWrapper} hidden lg:flex min-w-0 items-center flex-1 justify-center space-x-4 xl:space-x-8 text-sm font-medium`}
        >
          {Array.isArray(headerLinks) && headerLinks.length > 0 ? (
            <ConfigNav links={headerLinks} />
          ) : (
            <>
              <NavLink href="/" label="Home" />
              <ServicesMenu />
              <NavLink href="/support" label="Help & Support" />
              <NavLink href="/contact" label="Contact" />
              <NavLink href="/e-verify/documents" label="E-Verify" />
            </>
          )}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          {isLoggedIn ? (
            <div className="flex items-center gap-2">
              <MyAccountDropdown />
              <UserMenu user={user} compact />
            </div>
          ) : authHint && !authChecked ? (
            <AuthSkeleton />
          ) : (
            <>
              <ThemedButton
                variant="outlined"
                theme="accent"
                text="Log In"
                loadingOnNavigate
                href="/account/signin"
              />
              <ThemedButton
                theme="primary"
                text="Sign Up"
                loadingOnNavigate
                href="/account/signup"
              />
            </>
          )}
        </div>
      </div>

      <MobileMenu
        logo={imageSrc(cfg.logo)}
        name={cfg.name}
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        servicesOpen={servicesOpen}
        toggleServices={toggleServices}
        isLoggedIn={isLoggedIn}
        username={username}
      />
    </header>
  );
}
